<template>
  <div class="ridership-dashboard-module">
    <!-- Ridership by Route Card -->
    <div class="rd-card rd-card--main">
      <!-- Header: title and filters share one row per Zeplin (EdyGR5n) — title on
           the left, then date range / routes / direction, export pinned right. -->
      <div class="rd-card-header p-3 border-bottom">
        <div class="d-flex align-items-center gap-3 flex-wrap">
          <h6 class="dashboard-tile-title mb-0 me-2">{{ $t('flexigo.ridership.ridershipByRoute') }}</h6>
          <date-range-filter
            :selected-date="routeFilters.dateRange"
            default-selection="thisMonth"
            :last-date="new Date()"
            @change="handleRouteDateChange"
          />
          <toggle-button-group
            v-if="shouldShowRouteToggle"
            :options="workingTypeOptions"
            @change="onWorkingTypeChange"
          />

          <!-- Direction toggle (always visible, positioned before route dropdown) -->
          <ridership-direction-toggle
            :selected-direction="routeFilters.direction"
            :show-loop="hasLoopRoutes && selectedWorkingType !== 'Dynamic'"
            @change="handleRouteDirectionChange"
          />

          <!-- Route dropdown -->
          <route-multiselect
            :routes="routeCardRoutes"
            :selected-route-ids="routeFilters.routeIds"
            :no-routes="routeCardNoRoutes"
            :direction="routeFilters.direction"
            :working-type="selectedWorkingType"
            @change="handleRouteChange"
          />

          <!-- Export menu (PNG + Excel) — matches the OTP dashboard pattern.
               ms-auto pins it to the right edge of the flex row. -->
          <ridership-export-menu
            class="ms-auto"
            chart-ref="routeChart"
            filename="ridership-by-route"
            :loading="exporting"
            @export-excel="handleExportExcel"
          />
        </div>
      </div>

      <!-- View mode tabs + settings row -->
      <div class="p-3">
        <div class="d-flex gap-2 mb-3 flex-wrap align-items-center">
          <ridership-period-toggle :selected-period="routeFilters.routeViewMode" @change="setRouteViewMode" />

          <!-- Reference-time toggle: only relevant for multi-route, time-of-day or date views.
               Spec doesn't expose this on by-Weekday (no time-anchor effect) or single-route. -->
          <ridership-reference-time-toggle
            v-if="!isSingleRouteSelected && routeFilters.routeViewMode !== 'dayOfWeek'"
            :selected-ref-time="routeFilters.referenceTime"
            @change="handleRouteReferenceTimeChange"
          />

          <!-- From/To time range: not shown on by-Weekday (spec lines 81–97 list no time filter for byDay). -->
          <ridership-time-range-filter
            v-if="routeFilters.routeViewMode !== 'dayOfWeek'"
            :from-time="routeFilters.fromTime"
            :to-time="routeFilters.toTime"
            @change="handleRouteTimeRangeChange"
          />

          <ridership-interval-toggle
            v-if="routeFilters.routeViewMode === 'timeOfDay' && !isSingleRouteSelected"
            :selected-interval="routeFilters.interval"
            @change="handleIntervalChange"
          />
        </div>

        <div v-if="loading.trips" class="text-center py-5">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">{{ $t('flexigo.ridership.loading') }}</span>
          </div>
        </div>

        <ridership-by-route-chart
          v-else
          ref="routeChart"
          :categories="routeChartData.categories"
          :full-categories="routeChartData.fullCategories || []"
          :series="routeChartData.series"
          :occupancy="routeOccupancyData"
          :date-range="routeFilters.dateRange"
          :direction="chartDirection"
          :view-mode="chartViewMode"
          :reference-time="routeFilters.referenceTime"
          :from-time="routeFilters.fromTime"
          :to-time="routeFilters.toTime"
          :interval="routeFilters.interval"
          :is-single-route="isSingleRouteSelected"
          :route-name="singleRouteName"
        />

        <!-- Legend at bottom right -->
        <div class="d-flex justify-content-end mt-2">
          <route-legend :color-map="routeColorMap" />
        </div>
      </div>
    </div>

    <!-- Ridership by Stop Card -->
    <div class="rd-card mt-3">
      <div class="rd-card-header p-3 border-bottom">
        <div class="d-flex flex-wrap justify-content-between align-items-center gap-3">
          <h6 class="dashboard-tile-title mb-0">{{ $t('flexigo.ridership.ridershipByStop') }}</h6>
          <div class="d-flex flex-wrap gap-2 align-items-center">
            <date-range-filter
              :selected-date="stopFilters.dateRange"
              default-selection="thisMonth"
              :last-date="new Date()"
              @change="handleStopDateChange"
            />

            <toggle-button-group
              v-if="shouldShowStopToggle"
              :options="stopWorkingTypeOptions"
              @change="onStopWorkingTypeChange"
            />

            <route-multiselect
              :routes="stopCardRoutes"
              :selected-route-ids="stopFilters.routeIds"
              :no-routes="stopCardNoRoutes"
              :working-type="selectedStopWorkingType"
              @change="handleStopRouteChange"
            />
            <ridership-stop-filter :selected-stop-ids="stopFilters.stopIds" @change="handleStopChange" />

            <ridership-export-menu
              class="ms-auto"
              chart-ref="stopChart"
              filename="ridership-by-stop"
              :loading="stopExporting"
              @export-excel="handleStopExportExcel"
            />
          </div>
        </div>
      </div>

      <div class="p-3">
        <div class="d-flex gap-2 mb-3 flex-wrap align-items-center">
          <ridership-time-range-filter
            :from-time="stopFilters.fromTime"
            :to-time="stopFilters.toTime"
            @change="handleStopTimeRangeChange"
          />

          <div class="rd-sort-toggle" @click="toggleStopSort">
            {{ $t('flexigo.ridership.sortBy') }}
            <span>{{
              stopFilters.stopSortAsc ? $t('flexigo.ridership.ascending') : $t('flexigo.ridership.descending')
            }}</span>
            <span>{{ stopFilters.stopSortAsc ? '&#8593;' : '&#8595;' }}</span>
          </div>

          <toggle-button-group :options="stopMetricOptions" @change="handleStopMetricChange" />

          <!-- Sum shows the two grey On/Off tones; On/Off break the bars down by
               route, so they get the route-colour legend instead (design 5Ne1EkJ
               vs Dp7wlkW). -->
          <div class="ms-auto d-flex align-items-center gap-3">
            <template v-if="stopFilters.stopMetric === 'sum'">
              <div class="d-flex align-items-center gap-1">
                <span class="rd-legend-swatch rd-legend-swatch--striped"></span>
                <span class="rd-legend-label">{{ $t('flexigo.ridership.on') }}</span>
              </div>
              <div class="d-flex align-items-center gap-1">
                <span class="rd-legend-swatch" style="background: #687692"></span>
                <span class="rd-legend-label">{{ $t('flexigo.ridership.off') }}</span>
              </div>
            </template>
            <route-legend v-else :color-map="stopRouteColorMap" />
          </div>
        </div>

        <div v-if="loading.stops" class="text-center py-5">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">{{ $t('flexigo.ridership.loading') }}</span>
          </div>
        </div>

        <ridership-by-stop-chart
          v-else
          ref="stopChart"
          :categories="stopChartData.categories"
          :series="stopChartData.series"
          :metric="stopFilters.stopMetric"
          :date-range="stopFilters.dateRange"
          :route-color-map="routeColorMap"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { format, lastDayOfMonth } from 'date-fns';
import { getBrowserLocaleFormat } from '@/helpers/dateFormatter';
import { localFormatTimeString } from '@/helpers/dateTime';
import DateRangeFilter from '../../dateRangeFilter.vue';
import RouteMultiselect from '../RouteMultiselect.vue';
import RidershipDirectionToggle from './filters/RidershipDirectionToggle.vue';
import RidershipStopFilter from './filters/RidershipStopFilter.vue';
import RidershipPeriodToggle from './filters/RidershipPeriodToggle.vue';
import RidershipReferenceTimeToggle from './filters/RidershipReferenceTimeToggle.vue';
import RidershipTimeRangeFilter from './filters/RidershipTimeRangeFilter.vue';
import RidershipIntervalToggle from './filters/RidershipIntervalToggle.vue';
import RidershipByRouteChart from './charts/RidershipByRouteChart.vue';
import RidershipByStopChart from './charts/RidershipByStopChart.vue';
import RouteLegend from './components/RouteLegend.vue';
import RidershipExportMenu from './components/RidershipExportMenu.vue';
import toggleButtonGroup from '../../toggleButton/toggleButtonGroup.vue';

// Canonical weekday order (ISO, Monday-first). The "by Weekday" chart always
// renders the full Monday→Sunday axis per the Zeplin design, regardless of which
// days actually have trips — days without data show an empty (zero) bar slot.
const WEEKDAY_KEYS = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'];

/**
 * A stop earns a row in "Ridership by Stop" only once somebody used it. Stops the routes
 * passed without a single boarding or alighting in the selected range are left out rather
 * than listed as an empty bar (VSER-14676).
 */
const hasRidership = (stop) => (stop?.totalOn || 0) + (stop?.totalOff || 0) > 0;

// Fallback operation window used by both cards' From/To filters.
const DEFAULT_FROM_TIME = '06:00';
const DEFAULT_TO_TIME = '22:00';

/**
 * A card's dateRange from what date-range-filter emits ({ type, fromDate, toDate, startDay, endDay }).
 * fromDate/toDate are 'yyyy-MM-dd' strings, read as local dates; they stay on the range so the filter
 * can label a custom selection.
 */
function toCardDateRange({ type, fromDate, toDate, startDay, endDay }) {
  const [fromYear, fromMonth, fromDay] = fromDate.split('-').map(Number);
  const [toYear, toMonth, toDay] = toDate.split('-').map(Number);
  return {
    type,
    fromDate,
    toDate,
    startDay,
    endDay,
    startDate: new Date(fromYear, fromMonth - 1, fromDay),
    endDate: new Date(toYear, toMonth - 1, toDay),
  };
}

export default {
  name: 'RidershipDashboardModule',
  components: {
    DateRangeFilter,
    RouteMultiselect,
    RidershipDirectionToggle,
    RidershipStopFilter,
    RidershipPeriodToggle,
    RidershipReferenceTimeToggle,
    RidershipTimeRangeFilter,
    RidershipIntervalToggle,
    RidershipByRouteChart,
    RidershipByStopChart,
    RouteLegend,
    RidershipExportMenu,
    toggleButtonGroup,
  },
  props: {
    // Route ids to preselect in both cards' route filters, e.g. the routes of the route groups picked on
    // the dashboard. null leaves the cards on every route. The user can still change each card afterwards.
    presetRouteIds: {
      type: Array,
      default: null,
    },
    // Date range to preset on both cards, e.g. the dashboard header's, in date-range-filter's shape.
    // null keeps the cards on This Month. The user can still change each card afterwards.
    presetDateRange: {
      type: Object,
      default: null,
    },
  },
  data() {
    // Both cards open on the same default time window. The spec asks for the
    // company's operation hours, but CompanySettings exposes no such field
    // (see the VSER-13018 analysis), so this stays a product-level fallback
    // until the backend provides one. Seeded here rather than in mounted() so
    // the fromTime/toTime watchers don't fire a duplicate fetch on first render.
    const cs = this.$store?.state?.companySettings;
    const fromTime = cs?.operationStartTime || DEFAULT_FROM_TIME;
    const toTime = cs?.operationEndTime || DEFAULT_TO_TIME;
    // Default date range (This Month). Also seeded here rather than in
    // mounted() so the dateRange watchers don't fire a duplicate fetch
    // alongside the explicit mount fetch (VSER-14344).
    const today = new Date();
    const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    // "thisMonth" preset spans the full calendar month (see helpers/dateTime.js),
    // so initialize the range through month-end to match what reselecting the
    // preset would produce.
    const endOfMonth = lastDayOfMonth(today);
    const defaultRange = {
      // Match the DateRangeFilter's `default-selection="thisMonth"` so the tooltip's
      // period label resolves to the localized "This Month" instead of falling back
      // to a formatted date range on the very first render.
      type: 'thisMonth',
      startDay: Number(format(startOfMonth, 'yyyyMMdd')),
      endDay: Number(format(endOfMonth, 'yyyyMMdd')),
      startDate: startOfMonth,
      endDate: endOfMonth,
    };
    // An empty preset means the picked route groups have no routes in the range, so there's nothing to show.
    // An empty routeIds means every route, so the cards need to know the difference.
    const noPresetRoutes = Array.isArray(this.presetRouteIds) && this.presetRouteIds.length === 0;
    return {
      // Card shows no data (sends no request) until the user picks routes in its own filter.
      routeCardNoRoutes: noPresetRoutes,
      stopCardNoRoutes: noPresetRoutes,
      selectedWorkingType: 'All',
      selectedStopWorkingType: 'All',
      shouldShowRouteToggle: false,
      // Route module local filters (independent from Stop module)
      routeFilters: {
        // Both seeded from the presets here rather than in mounted() for the same reason as defaultRange:
        // the dashboard creates this module once the presets are known, and the mount-time fetch must use them.
        dateRange: this.presetDateRange ? toCardDateRange(this.presetDateRange) : defaultRange,
        routeIds: this.presetRouteIds ? [...this.presetRouteIds] : [],
        direction: 'ALL',
        routeViewMode: 'date',
        referenceTime: 'firstStopDeparture',
        fromTime,
        toTime,
        interval: '30min',
      },
      // Stop module local filters (independent from Route module)
      stopFilters: {
        dateRange: this.presetDateRange ? toCardDateRange(this.presetDateRange) : defaultRange,
        routeIds: this.presetRouteIds ? [...this.presetRouteIds] : [],
        stopIds: [],
        fromTime,
        toTime,
        stopMetric: 'sum',
        stopSortAsc: false,
      },
      exporting: false,
      stopExporting: false,
      // Routes fetched for the route card's own date range. The store's
      // availableRoutes is shared and also overwritten by the stop card's
      // refreshStopsList, so we keep a local copy to derive route-card-only
      // flags like hasLoopRoutes without being affected by stop-card fetches.
      routeCardRoutes: [],
      // Same reason, mirrored for the stop card: its date range is independent
      // from the route card's, so toggle visibility must derive from a stop-
      // card-scoped snapshot rather than the shared `availableRoutes` getter.
      stopCardRoutes: [],
    };
  },
  computed: {
    ...mapGetters('ridershipDashboard', [
      'rawTrips',
      'regionGroupedStops',
      'rawStops',
      'routeColorMap',
      'availableRoutes',
      'availableStops',
      'loading',
      'error',
    ]),
    workingTypeOptions() {
      return [
        {
          label: this.$t('flexigo.common.all'),
          selected: this.selectedWorkingType === 'All',
        },
        {
          label: this.$t('flexigo.workgroup.fixed'),
          selected: this.selectedWorkingType === 'Fixed',
        },
        {
          label: this.$t('flexigo.workgroup.dynamic'),
          selected: this.selectedWorkingType === 'Dynamic',
        },
      ];
    },
    stopWorkingTypeOptions() {
      return [
        {
          label: this.$t('flexigo.common.all'),
          selected: this.selectedStopWorkingType === 'All',
        },
        {
          label: this.$t('flexigo.workgroup.fixed'),
          selected: this.selectedStopWorkingType === 'Fixed',
        },
        {
          label: this.$t('flexigo.workgroup.dynamic'),
          selected: this.selectedStopWorkingType === 'Dynamic',
        },
      ];
    },
    // Route chart data processing (based on local routeFilters)
    dateRangeSpanDays() {
      const { dateRange } = this.routeFilters;
      if (!dateRange?.startDate || !dateRange?.endDate) return 0;
      return Math.round((dateRange.endDate - dateRange.startDate) / (1000 * 60 * 60 * 24)) + 1;
    },
    singleRouteName() {
      const { routeIds } = this.routeFilters;
      if (!routeIds.length) return '';
      const route = this.routeCardRoutes.find((r) => routeIds.includes(r.id));
      return route ? route.name : '';
    },
    /**
     * The colour of a single route's bars, stop segments included.
     *
     * Zeplin had the segments alternate orange and blue, which left every single-route bar
     * orange whatever colour the legend gave that route. The whole bar now carries the
     * route's own colour, so chart and legend agree (VSER-14676); the per-stop split is read
     * from the tooltip rather than from the shading.
     */
    singleRouteColor() {
      return this.routeColorMap[this.singleRouteName] || '#ff8b3d';
    },
    /**
     * The route's own direction when a single route is selected; falls back to
     * the user-chosen filter direction otherwise. Used as the chart's effective
     * direction so the single-route x-axis title (departure vs arrival) reflects
     * the route's actual direction even when the filter is 'ALL'.
     */
    chartDirection() {
      if (!this.isSingleRouteSelected) return this.routeFilters.direction;
      const { routeIds } = this.routeFilters;
      const route = this.routeCardRoutes.find((r) => routeIds.includes(r.id));
      return route?.direction || this.routeFilters.direction;
    },
    routeChartData() {
      if (!this.rawTrips?.length) return { categories: [], series: [] };
      const { routeViewMode } = this.routeFilters;
      if (this.isSingleRouteSelected) {
        if (routeViewMode === 'date') return this.singleRouteByDateData;
        if (routeViewMode === 'timeOfDay') return this.singleRouteByRunData;
        if (routeViewMode === 'dayOfWeek') return this.singleRouteByDayData;
      }
      if (routeViewMode === 'timeOfDay') return this.routeByRunData;
      if (routeViewMode === 'dayOfWeek') return this.routeByDayData;
      return this.routeByDateData;
    },
    routeByRunData() {
      const hourMap = new Map();
      const routeNames = new Set();
      const intervalMin = this.intervalMinutes();
      this.rawTrips.forEach((trip) => {
        if (!this.tripInTimeWindow(trip)) return;
        const bucketLabel = this.bucketTimeByInterval(this.tripReferenceTime(trip), intervalMin);
        if (!bucketLabel) return;
        routeNames.add(trip.routeName);
        if (!hourMap.has(bucketLabel)) hourMap.set(bucketLabel, {});
        const bucket = hourMap.get(bucketLabel);
        bucket[trip.routeName] = (bucket[trip.routeName] || 0) + (trip.totalOn || 0);
        const occKey = `${trip.routeName}__occ`;
        if (!bucket[occKey]) bucket[occKey] = { totalLoad: 0, count: 0 };
        if (trip.loadFactor != null && (trip.totalOn || 0) > 0) {
          bucket[occKey].totalLoad += trip.loadFactor;
          bucket[occKey].count += 1;
        }
      });
      const categories = Array.from(hourMap.keys()).sort();
      const sortedRoutes = Array.from(routeNames).sort((a, b) => a.localeCompare(b, 'tr-TR'));
      const series = sortedRoutes.map((name) => ({
        name,
        color: this.routeColorMap[name] || '#999',
        data: categories.map((h) => {
          const bucket = hourMap.get(h);
          const occ = bucket?.[`${name}__occ`];
          return {
            y: bucket?.[name] || 0,
            occupancy: occ && occ.count > 0 ? Math.round((occ.totalLoad / occ.count) * 100) : null,
          };
        }),
      }));
      return { categories, series };
    },
    routeByDayData() {
      const dayMap = new Map();
      const routeNames = new Set();
      this.rawTrips.forEach((trip) => {
        if (!trip.dayOfWeek) return;
        const dayKey = trip.dayOfWeek.toUpperCase();
        routeNames.add(trip.routeName);
        if (!dayMap.has(dayKey)) dayMap.set(dayKey, {});
        const bucket = dayMap.get(dayKey);
        bucket[trip.routeName] = (bucket[trip.routeName] || 0) + (trip.totalOn || 0);
        const occKey = `${trip.routeName}__occ`;
        if (!bucket[occKey]) bucket[occKey] = { totalLoad: 0, count: 0 };
        if (trip.loadFactor != null && (trip.totalOn || 0) > 0) {
          bucket[occKey].totalLoad += trip.loadFactor;
          bucket[occKey].count += 1;
        }
      });
      // Always render the full Monday→Sunday axis; days without trips show a zero bar.
      const categories = WEEKDAY_KEYS;
      const sortedRoutes = Array.from(routeNames).sort((a, b) => a.localeCompare(b, 'tr-TR'));
      const series = sortedRoutes.map((name) => ({
        name,
        color: this.routeColorMap[name] || '#999',
        data: categories.map((d) => {
          const bucket = dayMap.get(d);
          const occ = bucket?.[`${name}__occ`];
          return {
            y: bucket?.[name] || 0,
            occupancy: occ && occ.count > 0 ? Math.round((occ.totalLoad / occ.count) * 100) : null,
          };
        }),
      }));
      const displayCategories = categories.map((d) => this.localizedDayName(d));
      return { categories: displayCategories, series };
    },
    routeByDateData() {
      const bucketMap = new Map();
      const routeNames = new Set();
      const occupancyMap = new Map();
      const span = this.dateRangeSpanDays;
      // Pre-fill all bucket keys for the full date range
      const allKeys = this.generateAllBucketKeys(span);
      allKeys.forEach((key) => {
        bucketMap.set(key, {});
        occupancyMap.set(key, { totalLoad: 0, count: 0 });
      });
      this.rawTrips.forEach((trip) => {
        if (!this.tripInTimeWindow(trip)) return;
        routeNames.add(trip.routeName);
        const key = this.dateBucketKey(trip, span);
        if (!bucketMap.has(key)) bucketMap.set(key, {});
        const bucket = bucketMap.get(key);
        bucket[trip.routeName] = (bucket[trip.routeName] || 0) + (trip.totalOn || 0);
        const occKey = `${trip.routeName}__occ`;
        if (!bucket[occKey]) bucket[occKey] = { totalLoad: 0, count: 0 };
        // Only count occupancy for trips that actually carried passengers.
        // Trips with totalOn=0 (e.g. scheduled but empty weekend runs) have
        // loadFactor=0, which would produce 0% occupancy instead of a line
        // break (null). Skipping them ensures empty days show as gaps.
        if (trip.loadFactor != null && (trip.totalOn || 0) > 0) {
          bucket[occKey].totalLoad += trip.loadFactor;
          bucket[occKey].count += 1;
        }
        if (!occupancyMap.has(key)) occupancyMap.set(key, { totalLoad: 0, count: 0 });
        const occ = occupancyMap.get(key);
        if (trip.loadFactor != null && (trip.totalOn || 0) > 0) {
          occ.totalLoad += trip.loadFactor;
          occ.count += 1;
        }
      });
      const categories = allKeys.length > 0 ? allKeys : Array.from(bucketMap.keys()).sort();
      const displayCategories = categories.map((k) => this.dateBucketLabel(k, span, { short: true }));
      const fullCategories = categories.map((k) => this.dateBucketLabel(k, span));
      const sortedRoutes = Array.from(routeNames).sort((a, b) => a.localeCompare(b, 'tr-TR'));
      const series = sortedRoutes.map((name) => ({
        name,
        color: this.routeColorMap[name] || '#999',
        data: categories.map((k) => {
          const bucket = bucketMap.get(k);
          const occ = bucket?.[`${name}__occ`];
          return {
            y: bucket?.[name] || 0,
            occupancy: occ && occ.count > 0 ? Math.round((occ.totalLoad / occ.count) * 100) : null,
          };
        }),
      }));
      // null (not 0) on days without data so the occupancy LINE breaks instead
      // of dropping to 0% — bars still show an empty 0 slot, but the line must
      // visually distinguish "no trips" from "0% occupancy" (QA finding).
      const occupancy = categories.map((k) => {
        const occ = occupancyMap.get(k);
        return !occ || occ.count === 0 ? null : Math.round((occ.totalLoad / occ.count) * 100);
      });
      return {
        categories: displayCategories,
        fullCategories,
        series,
        occupancy,
      };
    },
    singleRouteByRunData() {
      const stopOrder = [];
      const stopNameSet = new Set();
      const hourMap = new Map();
      const statsMap = new Map();
      // Per spec line 69: single-route by-time-of-day shows EXACT scheduled times
      // (each distinct scheduled departure/arrival is its own x-axis bar).
      this.rawTrips.forEach((trip) => {
        if (!this.tripInTimeWindow(trip)) return;
        const timeLabel = this.singleRouteTimeKey(trip);
        if (!timeLabel) return;
        if (!hourMap.has(timeLabel)) hourMap.set(timeLabel, {});
        if (!statsMap.has(timeLabel)) statsMap.set(timeLabel, { totalOn: 0, loadFactorSum: 0, loadFactorCount: 0 });
        const bucket = hourMap.get(timeLabel);
        const stats = statsMap.get(timeLabel);
        stats.totalOn += trip.totalOn || 0;
        if (trip.loadFactor != null && (trip.totalOn || 0) > 0) {
          stats.loadFactorSum += trip.loadFactor;
          stats.loadFactorCount += 1;
        }
        this.tripBoardingStops(trip).forEach((stop) => {
          if (!stopNameSet.has(stop.stopName)) {
            stopNameSet.add(stop.stopName);
            stopOrder.push(stop.stopName);
          }
          bucket[stop.stopName] = (bucket[stop.stopName] || 0) + (stop.totalOn || 0);
        });
      });
      const categories = Array.from(hourMap.keys()).sort();
      // Per-bar breakdown shared across every stacked stop point at that x.
      const bucketBreakdown = categories.map((k) => {
        const stats = statsMap.get(k);
        const stops = stopOrder.map((name) => ({ name, value: hourMap.get(k)?.[name] || 0 }));
        const loadFactor =
          stats && stats.loadFactorCount > 0 ? Math.round((stats.loadFactorSum / stats.loadFactorCount) * 100) : null;
        return { stops, totalOn: stats?.totalOn || 0, loadFactor };
      });
      const series = stopOrder.map((name) => ({
        name,
        color: this.singleRouteColor,
        data: categories.map((k, idx) => ({
          y: hourMap.get(k)?.[name] || 0,
          bucketData: bucketBreakdown[idx],
        })),
      }));
      return { categories, series };
    },
    singleRouteByDayData() {
      const stopOrder = [];
      const stopNameSet = new Set();
      const dayMap = new Map();
      const statsMap = new Map();
      this.rawTrips.forEach((trip) => {
        if (!trip.dayOfWeek) return;
        const dayKey = trip.dayOfWeek.toUpperCase();
        if (!dayMap.has(dayKey)) dayMap.set(dayKey, {});
        if (!statsMap.has(dayKey)) statsMap.set(dayKey, { totalOn: 0, loadFactorSum: 0, loadFactorCount: 0 });
        const bucket = dayMap.get(dayKey);
        const stats = statsMap.get(dayKey);
        stats.totalOn += trip.totalOn || 0;
        if (trip.loadFactor != null && (trip.totalOn || 0) > 0) {
          stats.loadFactorSum += trip.loadFactor;
          stats.loadFactorCount += 1;
        }
        this.tripBoardingStops(trip).forEach((stop) => {
          if (!stopNameSet.has(stop.stopName)) {
            stopNameSet.add(stop.stopName);
            stopOrder.push(stop.stopName);
          }
          bucket[stop.stopName] = (bucket[stop.stopName] || 0) + (stop.totalOn || 0);
        });
      });
      // Full Monday→Sunday axis even when a single route only runs on some days.
      const categories = WEEKDAY_KEYS;
      const displayCategories = categories.map((d) => this.localizedDayName(d));
      const bucketBreakdown = categories.map((d) => {
        const stats = statsMap.get(d);
        const stops = stopOrder.map((name) => ({ name, value: dayMap.get(d)?.[name] || 0 }));
        const loadFactor =
          stats && stats.loadFactorCount > 0 ? Math.round((stats.loadFactorSum / stats.loadFactorCount) * 100) : null;
        return { stops, totalOn: stats?.totalOn || 0, loadFactor };
      });
      const series = stopOrder.map((name) => ({
        name,
        color: this.singleRouteColor,
        data: categories.map((d, idx) => ({
          y: dayMap.get(d)?.[name] || 0,
          bucketData: bucketBreakdown[idx],
        })),
      }));
      return { categories: displayCategories, series };
    },
    singleRouteByDateData() {
      const stopNameSet = new Set();
      const stopOrder = [];
      const bucketMap = new Map();
      const bucketStats = new Map();
      const span = this.dateRangeSpanDays;
      const allKeys = this.generateAllBucketKeys(span);
      allKeys.forEach((key) => {
        bucketMap.set(key, {});
        bucketStats.set(key, { totalOn: 0, loadFactorSum: 0, loadFactorCount: 0 });
      });
      this.rawTrips.forEach((trip) => {
        if (!this.tripInTimeWindow(trip)) return;
        const key = this.dateBucketKey(trip, span);
        if (!bucketMap.has(key)) bucketMap.set(key, {});
        if (!bucketStats.has(key)) bucketStats.set(key, { totalOn: 0, loadFactorSum: 0, loadFactorCount: 0 });
        const bucket = bucketMap.get(key);
        const stats = bucketStats.get(key);
        stats.totalOn += trip.totalOn || 0;
        if (trip.loadFactor != null && (trip.totalOn || 0) > 0) {
          stats.loadFactorSum += trip.loadFactor;
          stats.loadFactorCount += 1;
        }
        this.tripBoardingStops(trip).forEach((stop) => {
          if (!stopNameSet.has(stop.stopName)) {
            stopNameSet.add(stop.stopName);
            stopOrder.push(stop.stopName);
          }
          bucket[stop.stopName] = (bucket[stop.stopName] || 0) + (stop.totalOn || 0);
        });
      });
      const categories = allKeys.length > 0 ? allKeys : Array.from(bucketMap.keys()).sort();
      const displayCategories = categories.map((k) => this.dateBucketLabel(k, span, { short: true }));
      const fullCategories = categories.map((k) => this.dateBucketLabel(k, span));
      // Pre-compute full per-bucket breakdown (shared on every point)
      const bucketBreakdown = categories.map((k) => {
        const stops = stopOrder.map((name) => ({ name, value: bucketMap.get(k)?.[name] || 0 }));
        const stats = bucketStats.get(k) || { totalOn: 0, loadFactorSum: 0, loadFactorCount: 0 };
        const loadFactor =
          stats.loadFactorCount > 0 ? Math.round((stats.loadFactorSum / stats.loadFactorCount) * 100) : null;
        return { stops, totalOn: stats.totalOn, loadFactor };
      });
      const series = stopOrder.map((name) => ({
        name,
        color: this.singleRouteColor,
        data: categories.map((k, idx) => ({
          y: bucketMap.get(k)?.[name] || 0,
          bucketData: bucketBreakdown[idx],
        })),
      }));
      return { categories: displayCategories, fullCategories, series };
    },
    routeOccupancyData() {
      if (!this.rawTrips?.length) return [];
      const { routeViewMode } = this.routeFilters;
      if (routeViewMode === 'timeOfDay') return this.occupancyByRunData;
      if (routeViewMode === 'dayOfWeek') return this.occupancyByDayData;
      if (routeViewMode === 'date') return this.routeByDateData.occupancy;
      return [];
    },
    occupancyByRunData() {
      const hourMap = new Map();
      // Single-route uses exact scheduled times; multi-route uses the interval bucket.
      const useExactTimes = this.isSingleRouteSelected;
      const intervalMin = this.intervalMinutes();
      this.rawTrips.forEach((trip) => {
        if (!this.tripInTimeWindow(trip)) return;
        const bucketLabel = useExactTimes
          ? this.singleRouteTimeKey(trip)
          : this.bucketTimeByInterval(this.tripReferenceTime(trip), intervalMin);
        if (!bucketLabel) return;
        if (!hourMap.has(bucketLabel)) hourMap.set(bucketLabel, { totalLoad: 0, count: 0 });
        const bucket = hourMap.get(bucketLabel);
        if (trip.loadFactor != null && (trip.totalOn || 0) > 0) {
          bucket.totalLoad += trip.loadFactor;
          bucket.count += 1;
        }
      });
      const categories = Array.from(hourMap.keys()).sort();
      return categories.map((h) => {
        const occ = hourMap.get(h);
        return !occ || occ.count === 0 ? null : Math.round((occ.totalLoad / occ.count) * 100);
      });
    },
    occupancyByDayData() {
      const dayMap = new Map();
      this.rawTrips.forEach((trip) => {
        if (!trip.dayOfWeek) return;
        const dayKey = trip.dayOfWeek.toUpperCase();
        if (!dayMap.has(dayKey)) dayMap.set(dayKey, { totalLoad: 0, count: 0 });
        const bucket = dayMap.get(dayKey);
        if (trip.loadFactor != null && (trip.totalOn || 0) > 0) {
          bucket.totalLoad += trip.loadFactor;
          bucket.count += 1;
        }
      });
      // Aligns with the full Monday→Sunday bar axis (null on days without data
      // so the occupancy line breaks rather than showing 0%).
      return WEEKDAY_KEYS.map((d) => {
        const occ = dayMap.get(d);
        return !occ || occ.count === 0 ? null : Math.round((occ.totalLoad / occ.count) * 100);
      });
    },
    chartViewMode() {
      const { routeViewMode } = this.routeFilters;
      if (routeViewMode === 'timeOfDay') return 'byRun';
      if (routeViewMode === 'dayOfWeek') return 'byDay';
      return 'byDate';
    },
    stopMetricOptions() {
      const { stopMetric } = this.stopFilters;
      return [
        { value: 'sum', label: this.$t('flexigo.ridership.sum') },
        { value: 'on', label: this.$t('flexigo.ridership.on') },
        { value: 'off', label: this.$t('flexigo.ridership.off') },
      ].map((opt) => ({ ...opt, selected: opt.value === stopMetric }));
    },
    // Stop chart data processing (based on local stopFilters).
    // Each data point carries `routeBreakdown` so the tooltip can show the
    // per-route on/off split required by the spec (lines 108–113).
    // Reads `regionGroupedStops`, not `rawStops`: stops of dynamic routes are
    // already collapsed into one row per region there, labelled with the
    // region name (VSER-14220). Plain stops come through unchanged.
    stopChartData() {
      if (!this.regionGroupedStops?.length) return { categories: [], series: [] };
      const { stopMetric, stopSortAsc } = this.stopFilters;
      const data = this.regionGroupedStops.filter(hasRidership).map((stop) => {
        // Loop routes share the same `routeName` across multiple ringIds — collapse them
        // into a single row per spec line 15 ("Aynı isimdeki loop rotalar tek satır").
        const routeAgg = new Map();
        (stop.routes || []).forEach((r) => {
          if (!r.routeName) return;
          const existing = routeAgg.get(r.routeName);
          if (existing) {
            existing.totalOn += r.totalOn || 0;
            existing.totalOff += r.totalOff || 0;
          } else {
            routeAgg.set(r.routeName, {
              routeName: r.routeName,
              totalOn: r.totalOn || 0,
              totalOff: r.totalOff || 0,
            });
          }
        });
        return {
          stopName: stop.stopName,
          totalOn: stop.totalOn || 0,
          totalOff: stop.totalOff || 0,
          sum: (stop.totalOn || 0) + (stop.totalOff || 0),
          routes: Array.from(routeAgg.values()),
        };
      });
      let sortKey = 'sum';
      if (stopMetric === 'on') sortKey = 'totalOn';
      else if (stopMetric === 'off') sortKey = 'totalOff';
      data.sort((a, b) => (stopSortAsc ? a[sortKey] - b[sortKey] : b[sortKey] - a[sortKey]));
      const categories = data.map((s) => s.stopName);

      // Per-bar `routeBreakdown` is the same on every stacked segment of the bar;
      // it carries the full per-route breakdown plus aggregate totals so the
      // tooltip formatter doesn't have to re-derive anything from chart points.
      const routeBreakdown = data.map((s) => ({
        totalOn: s.totalOn,
        totalOff: s.totalOff,
        routes: s.routes,
      }));

      const buildPoints = (values) => values.map((y, idx) => ({ y, routeBreakdown: routeBreakdown[idx] }));

      let series;
      if (stopMetric === 'sum') {
        series = [
          {
            name: 'On',
            // Diagonal stripe pattern per QA request: 1px wide continuous
            // 45° lines with ~2px gap, on a pale-grey (#f6f7f9) background.
            // The 4×4 tile uses two path segments so the diagonal continues
            // seamlessly across tile boundaries. Stripe color #687692 matches
            // the Zeplin bar color (design token blue-grey).
            color: {
              pattern: {
                backgroundColor: '#bcc2cd',
                color: '#687692',
                path: {
                  d: 'M 0 3 L 3 0 M 3 4 L 4 3',
                  strokeWidth: 1,
                },
                width: 4,
                height: 4,
              },
            },
            data: buildPoints(data.map((s) => s.totalOn)),
          },
          { name: 'Off', color: '#687692', data: buildPoints(data.map((s) => s.totalOff)) },
        ];
      } else {
        // On / Off: the bar is broken down by route in route colours, not shown
        // as one flat grey bar (design Dp7wlkW). One series per route name,
        // stacked in the same order as the route legend.
        const metricKey = stopMetric === 'on' ? 'totalOn' : 'totalOff';
        series = this.stopRouteNames.map((routeName) => ({
          name: routeName,
          color: this.routeColorMap[routeName] || '#999',
          data: data.map((s, idx) => ({
            y: s.routes.find((r) => r.routeName === routeName)?.[metricKey] || 0,
            routeBreakdown: routeBreakdown[idx],
          })),
        }));
      }
      return { categories, series };
    },
    /**
     * Route names present in the current stop data, alphabetically ordered.
     * Drives both the On/Off stacking order and the route-colour legend, so
     * the legend never lists routes that aren't in the chart.
     */
    stopRouteNames() {
      const names = new Set();
      // Same rows the chart draws, so a route that only serves stops with no ridership
      // does not linger in the stacking order or the colour legend.
      (this.regionGroupedStops || []).filter(hasRidership).forEach((stop) => {
        (stop.routes || []).forEach((r) => {
          if (r.routeName) names.add(r.routeName);
        });
      });
      return Array.from(names).sort((a, b) => a.localeCompare(b, 'tr-TR'));
    },
    stopRouteColorMap() {
      return this.stopRouteNames.reduce((acc, name) => ({ ...acc, [name]: this.routeColorMap[name] || '#999' }), {});
    },
    isSingleRouteSelected() {
      const { routeIds } = this.routeFilters;
      if (routeIds.length === 0) return false;
      const selectedNames = new Set(
        routeIds
          .map((id) => this.routeCardRoutes.find((r) => r.id === id))
          .filter(Boolean)
          .map((r) => r.name),
      );
      return selectedNames.size === 1;
    },
    /**
     * Whether the company has any loop routes in the current date range.
     * Per spec note: the LOOP direction option should be hidden when no loop
     * routes exist. Route objects carry an `isLoop` flag from the BE
     * (see RIDERSHIP_API_SPEC.md §3).
     */
    hasLoopRoutes() {
      return this.routeCardRoutes.some((r) => r.isLoop);
    },
    /**
     * Stop card working-type toggle visibility.
     *
     * Derived from `stopCardRoutes` (routes available for the stop card's
     * own date range) and the current route selection. The shared store
     * getter `availableRoutes` is deliberately NOT used because the route
     * card's independent date range can overwrite it. The stop filter is
     * also NOT a factor: selecting a specific stop restricts the chart but
     * shouldn't lock the toggle to the stop's current slice. When a specific
     * route is selected, the fetch is already narrowed to that route's
     * working type, so the toggle has no additional effect and is hidden.
     */
    shouldShowStopToggle() {
      if (this.stopFilters.routeIds.length > 0) return false;
      const hasFixed = this.stopCardRoutes.some((r) => r.workingType === 'FIXED');
      const hasDynamic = this.stopCardRoutes.some((r) => r.workingType === 'DYNAMIC');
      return hasFixed && hasDynamic;
    },
  },
  watch: {
    // Assigning dateRange fires the cards' dateRange watchers below, which refetch when the days changed.
    presetDateRange(dateRange) {
      if (!dateRange) return;
      this.routeFilters.dateRange = toCardDateRange(dateRange);
      this.stopFilters.dateRange = toCardDateRange(dateRange);
    },
    // Assigning routeIds fires the cards' routeIds watchers below, which refetch.
    presetRouteIds(routeIds) {
      const ids = routeIds || [];
      const idSet = new Set(ids);
      const sameIds = (current) => current.length === idSet.size && current.every((id) => idSet.has(id));
      const noRoutes = Array.isArray(routeIds) && routeIds.length === 0;
      const routeCardModeChanges = this.routeCardNoRoutes !== noRoutes;
      const stopCardModeChanges = this.stopCardNoRoutes !== noRoutes;
      // Set before routeIds, so the fetches the routeIds watchers trigger already see it.
      this.routeCardNoRoutes = noRoutes;
      this.stopCardNoRoutes = noRoutes;
      // Unchanged ids fire no watcher, so a switch between "every route" and "no routes" fetches here.
      if (!sameIds(this.routeFilters.routeIds)) this.routeFilters.routeIds = [...ids];
      else if (routeCardModeChanges) this.fetchRouteTrips();
      if (!sameIds(this.stopFilters.routeIds)) this.stopFilters.routeIds = [...ids];
      else if (stopCardModeChanges) this.fetchStopChart();
    },
    // If LOOP was selected but loop routes disappear (e.g. date range change),
    // reset to ALL so the hidden toggle option doesn't leave a stale filter.
    hasLoopRoutes(newVal) {
      if (!newVal && this.routeFilters.direction === 'LOOP') {
        this.routeFilters.direction = 'ALL';
      }
    },
    'routeFilters.dateRange': {
      deep: true,
      handler(newVal, oldVal) {
        // Only fetch if dateRange actually changed
        if (!oldVal || newVal?.startDay !== oldVal?.startDay || newVal?.endDay !== oldVal?.endDay) {
          // Reset the auto-selected working type so the new range is refetched
          // with DYNAMIC,FIXED and the rawTrips watcher can re-detect which
          // types actually exist in the new period (VSER-14344).
          this.resetRouteWorkingTypeFilter();
          this.fetchRouteData();
        }
      },
    },
    'routeFilters.routeIds': {
      deep: true,
      handler() {
        // Route selection can broaden/narrow the available working types, so
        // reset the auto-selected type and toggle visibility; the rawTrips
        // watcher will re-detect what the new selection actually contains.
        this.resetRouteWorkingTypeFilter();
        this.fetchRouteTrips();
      },
    },
    'routeFilters.direction': {
      handler(newDir) {
        // Clear selected routes that don't match the new direction
        if (newDir !== 'ALL' && this.routeFilters.routeIds.length > 0) {
          const selectedRoutes = this.routeCardRoutes.filter((route) => this.routeFilters.routeIds.includes(route.id));
          const hasIncompatibleRoutes = selectedRoutes.some((route) => route.direction !== newDir);

          if (hasIncompatibleRoutes) {
            this.routeFilters.routeIds = [];
          }
        }

        // Reset the auto-selected working type so the new direction is
        // refetched with DYNAMIC,FIXED. Otherwise a type that was auto-set
        // from the previous direction's data would narrow this request and
        // keep the toggle hidden, locking the user out of other types.
        this.resetRouteWorkingTypeFilter();

        // Spec: Inbound auto-selects "by Last Stop Arrival". When this assignment
        // changes referenceTime, the referenceTime watcher will trigger the
        // refetch so we don't fire two parallel /ridership/trips calls.
        if (newDir === 'INCOMING' && this.routeFilters.referenceTime !== 'lastStopArrival') {
          this.routeFilters.referenceTime = 'lastStopArrival';
          return;
        }
        this.fetchRouteTrips();
      },
    },
    'routeFilters.referenceTime': {
      handler() {
        // Toggle changes which time field the chart anchors on. The BE only
        // filters on scheduledStart, so when switching to lastStopArrival we
        // need to refetch without fromTime/toTime so FE can post-filter by
        // scheduledEnd. See fetchRouteTrips for the param logic.
        this.fetchRouteTrips();
      },
    },
    selectedWorkingType(newType) {
      // Auto-set from observed data (rawTrips watcher) narrows the backend
      // param to a type the current response already contains in full — a
      // refetch would return the same rows, so skip it (VSER-14344).
      if (this.skipNextWorkingTypeFetch) {
        this.skipNextWorkingTypeFetch = false;
        return;
      }

      // Clear selected routes if they don't match the new working type
      if (this.routeFilters.routeIds.length > 0 && newType !== 'All') {
        const targetType = newType.toUpperCase(); // 'Dynamic' -> 'DYNAMIC', 'Fixed' -> 'FIXED'
        const selectedRoutes = this.routeCardRoutes.filter((route) => this.routeFilters.routeIds.includes(route.id));
        const hasIncompatibleRoutes = selectedRoutes.some((route) => route.workingType !== targetType);

        if (hasIncompatibleRoutes) {
          this.routeFilters.routeIds = [];
        }
      }

      // If Dynamic is selected and current direction is LOOP, reset to 'ALL'
      if (newType === 'Dynamic' && this.routeFilters.direction === 'LOOP') {
        this.routeFilters.direction = 'ALL';
        // Return early - the direction watcher will trigger the fetch
        return;
      }
      this.fetchRouteTrips();
    },
    rawTrips: {
      immediate: true,
      handler(newTrips) {
        // Set shouldShowRouteToggle only once on initial load
        if (newTrips && newTrips.length > 0 && !this.shouldShowRouteToggle) {
          // Check workingTypes from stops array within trips
          const hasFixed = newTrips.some((trip) => trip.stops?.some((stop) => stop.workingType === 'FIXED'));
          const hasDynamic = newTrips.some((trip) => trip.stops?.some((stop) => stop.workingType === 'DYNAMIC'));

          this.shouldShowRouteToggle = hasFixed && hasDynamic;

          // Auto-set selectedWorkingType based on available data. The current
          // response already contains every row the narrowed param would
          // return, so suppress the watcher's refetch to avoid a duplicate
          // /ridership/trips call (VSER-14344).
          if (!hasFixed && hasDynamic) {
            this.skipNextWorkingTypeFetch = true;
            this.selectedWorkingType = 'Dynamic';
          } else if (hasFixed && !hasDynamic) {
            this.skipNextWorkingTypeFetch = true;
            this.selectedWorkingType = 'Fixed';
          }
        }
      },
    },
    'routeFilters.fromTime': {
      handler() {
        this.fetchRouteTrips();
      },
    },
    'routeFilters.toTime': {
      handler() {
        this.fetchRouteTrips();
      },
    },
    'stopFilters.dateRange': {
      deep: true,
      handler(newVal, oldVal) {
        // Only fetch if dateRange actually changed
        if (!oldVal || newVal?.startDay !== oldVal?.startDay || newVal?.endDay !== oldVal?.endDay) {
          // See the route card's dateRange watcher — same reset so the new
          // range is refetched with DYNAMIC,FIXED and the rawStops watcher
          // re-detects which types exist in the new period (VSER-14344).
          this.resetStopWorkingTypeFilter();
          this.fetchStopData();
        }
      },
    },
    'stopFilters.routeIds': {
      deep: true,
      handler() {
        // Route selection narrows the stop response to that route's working
        // type, so reset the toggle and let the rawStops watcher re-detect
        // which types the new selection actually contains — matches the
        // route card's routeIds watcher.
        this.resetStopWorkingTypeFilter();
        this.fetchStopData();
      },
    },
    // The stop selection and the time window don't affect the stop dropdown
    // list (BE list endpoint only takes date range + routeIds), so these
    // refetch the chart data only.
    'stopFilters.stopIds': {
      deep: true,
      handler() {
        this.fetchStopChart();
      },
    },
    'stopFilters.fromTime': {
      handler() {
        this.fetchStopChart();
      },
    },
    'stopFilters.toTime': {
      handler() {
        this.fetchStopChart();
      },
    },
    selectedStopWorkingType(newType) {
      // See the selectedWorkingType watcher — same suppression for the stop
      // chart's auto-set from observed data (VSER-14344).
      if (this.skipNextStopWorkingTypeFetch) {
        this.skipNextStopWorkingTypeFetch = false;
        return;
      }

      // Clear selected routes if they don't match the new working type
      if (this.stopFilters.routeIds.length > 0 && newType !== 'All') {
        const targetType = newType.toUpperCase(); // 'Dynamic' -> 'DYNAMIC', 'Fixed' -> 'FIXED'
        const selectedRoutes = this.stopCardRoutes.filter((route) => this.stopFilters.routeIds.includes(route.id));
        const hasIncompatibleRoutes = selectedRoutes.some((route) => route.workingType !== targetType);

        if (hasIncompatibleRoutes) {
          this.stopFilters.routeIds = [];
        }
      }

      this.fetchStopChart();
    },
    rawStops: {
      immediate: true,
      handler(newStops) {
        // Auto-narrow selectedStopWorkingType to the only type present in the
        // current response, so chart tooltips/legends don't mislabel data as
        // "All". Visibility of the toggle itself is a computed derived from
        // availableRoutes, not from this stop-filtered response.
        if (!newStops || newStops.length === 0) return;
        if (this.selectedStopWorkingType !== 'All') return;
        const hasFixed = newStops.some((stop) => stop.routes?.some((route) => route.workingType === 'FIXED'));
        const hasDynamic = newStops.some((stop) => stop.routes?.some((route) => route.workingType === 'DYNAMIC'));
        // See the rawTrips watcher — suppress the refetch when narrowing
        // the param to a type the current response already contains in full.
        if (!hasFixed && hasDynamic) {
          this.skipNextStopWorkingTypeFetch = true;
          this.selectedStopWorkingType = 'Dynamic';
        } else if (hasFixed && !hasDynamic) {
          this.skipNextStopWorkingTypeFetch = true;
          this.selectedStopWorkingType = 'Fixed';
        }
      },
    },
  },
  // The store outlives this module, and the dashboard rebuilds it on every header filter change. The
  // immediate rawTrips / rawStops watchers would read the previous view's data and narrow the working
  // types of the first requests to it (FIXED for a fixed-only group, say, while the new one is dynamic).
  beforeCreate() {
    this.$store.commit('ridershipDashboard/CLEAR_DATA');
  },
  // A lookup still in flight when the dashboard is rebuilt resolves after this module is gone; its
  // continuation must not dispatch chart requests with the old filters (they'd be the newest request).
  beforeDestroy() {
    this.disposed = true;
  },
  mounted() {
    // All default filter values (dateRange, fromTime, toTime) are seeded in
    // data() so no watcher fires a duplicate fetch on first render.
    this.fetchRouteData();
    this.fetchStopData();
  },
  methods: {
    ...mapActions('ridershipDashboard', [
      'fetchTrips',
      'fetchStops',
      'clearTrips',
      'clearStops',
      'fetchRoutes',
      'fetchStopsList',
      'exportExcel',
    ]),
    // Clear the auto-selected working type and hide the toggle so the next
    // fetch goes out with DYNAMIC,FIXED and the rawTrips watcher re-detects
    // which types the new filter combination actually contains. The skip
    // flag is only armed when the assignment will actually change the value;
    // otherwise the watcher never fires and the flag stays stuck armed,
    // swallowing the user's next Fixed/Dynamic selection.
    resetRouteWorkingTypeFilter() {
      if (this.selectedWorkingType !== 'All') {
        this.skipNextWorkingTypeFetch = true;
        this.selectedWorkingType = 'All';
      }
      this.shouldShowRouteToggle = false;
    },
    resetStopWorkingTypeFilter() {
      if (this.selectedStopWorkingType !== 'All') {
        this.skipNextStopWorkingTypeFetch = true;
        this.selectedStopWorkingType = 'All';
      }
    },
    // Route module data fetching
    async fetchRouteData() {
      const { startDay, endDay } = this.routeFilters.dateRange || {};
      if (!startDay || !endDay) return;
      const routes = await this.fetchRoutes({ startDay, endDay });
      if (this.disposed) return;
      // Snapshot the routes fetched for the route card's date range so
      // hasLoopRoutes isn't affected when the stop card overwrites the
      // shared store state via refreshStopsList. Taken from what this fetch
      // returned: the store may already hold the stop card's list by now.
      // A failed lookup keeps the card's previous list: the shared one may be the other card's.
      if (routes) this.routeCardRoutes = [...routes];
      // Normalize direction synchronously: if LOOP was selected but loop
      // routes no longer exist, reset to ALL and let the direction watcher
      // issue the replacement fetch. Otherwise the async hasLoopRoutes
      // watcher would fire too late, causing a stale LOOP request followed
      // by a second ALL request with no stale-response guard.
      if (!this.hasLoopRoutes && this.routeFilters.direction === 'LOOP') {
        this.routeFilters.direction = 'ALL';
        return;
      }
      await this.fetchRouteTrips();
    },
    async fetchRouteTrips() {
      if (this.routeCardNoRoutes) {
        this.clearTrips();
        return;
      }
      const { startDay, endDay } = this.routeFilters.dateRange || {};
      if (!startDay || !endDay) return;
      const { routeIds, direction, fromTime, toTime, referenceTime } = this.routeFilters;
      // BE filters trips by `scheduledStart` against fromTime/toTime and has no
      // toggle for the reference field. For `byLastStopArrival` we need the
      // window applied to `scheduledEnd`, so we fetch the full day-range and
      // let `tripInTimeWindow` post-filter on FE. Verified live against
      // /ridership/trips: no `referenceTime` query param is honoured.
      const useBETimeFilter = referenceTime !== 'lastStopArrival';

      // Map selectedWorkingType to backend workingTypes parameter
      let workingTypes;
      if (this.selectedWorkingType === 'Fixed') {
        workingTypes = 'FIXED';
      } else if (this.selectedWorkingType === 'Dynamic') {
        workingTypes = 'DYNAMIC';
      } else {
        workingTypes = 'DYNAMIC,FIXED';
      }

      await this.fetchTrips({
        startDay,
        endDay,
        routeIds,
        direction,
        fromTime: useBETimeFilter ? fromTime : undefined,
        toTime: useBETimeFilter ? toTime : undefined,
        workingTypes,
      });
    },
    // Stop module data fetching (date range / route filter changes)
    async fetchStopData() {
      const selectionChanged = await this.refreshStopsList();
      if (this.disposed) return;
      // A remapped selection refetches the chart via the stopIds watcher.
      if (!selectionChanged) await this.fetchStopChart();
    },
    /**
     * Refresh the stop dropdown for the current date range / route filter,
     * then re-resolve the selected stop against the new list BY NAME.
     *
     * The BE list endpoint (/ridership/stops/list) dedupes stops by name and
     * returns the minimum stopId among the rows that survive the route
     * filter, so the id of the same named stop changes when the route filter
     * changes. Re-selecting by name keeps the dropdown, the filter state and
     * the /ridership/stops fetch consistent (VSER-12588 QA finding 3).
     *
     * Returns true when stopIds was reassigned; the caller must then skip
     * its own chart fetch because the stopIds watcher already triggers one.
     */
    async refreshStopsList() {
      const { startDay, endDay } = this.stopFilters.dateRange || {};
      if (!startDay || !endDay) return false;
      const { routeIds, stopIds } = this.stopFilters;
      const prevId = stopIds.length ? stopIds[0] : null;
      const prevName = prevId != null ? this.availableStops.find((s) => s.id === prevId)?.name : null;
      const [routes] = await Promise.all([
        this.fetchRoutes({ startDay, endDay }),
        this.fetchStopsList({ startDay, endDay, routeIds }),
      ]);
      if (this.disposed) return false;
      // Snapshot the routes fetched for the stop card's date range. See
      // routeCardRoutes above — same reason, mirrored, and likewise taken
      // from what this fetch returned.
      if (routes) this.stopCardRoutes = [...routes];
      if (prevId == null) return false;
      const match = prevName != null ? this.availableStops.find((s) => s.name === prevName) : null;
      const changed = (match ? match.id : null) !== prevId;
      if (changed) this.stopFilters.stopIds = match ? [match.id] : [];
      return changed;
    },
    async fetchStopChart() {
      if (this.stopCardNoRoutes) {
        this.clearStops();
        return;
      }
      const { startDay, endDay } = this.stopFilters.dateRange || {};
      if (!startDay || !endDay) return;
      const { routeIds, stopIds, fromTime, toTime } = this.stopFilters;

      // Map selectedStopWorkingType to backend workingTypes parameter
      let workingTypes;
      if (this.selectedStopWorkingType === 'Fixed') {
        workingTypes = 'FIXED';
      } else if (this.selectedStopWorkingType === 'Dynamic') {
        workingTypes = 'DYNAMIC';
      } else {
        workingTypes = 'DYNAMIC,FIXED';
      }

      await this.fetchStops({ startDay, endDay, routeIds, stopIds, fromTime, toTime, workingTypes });
    },
    // Utility methods for data processing
    extractHour(timeStr) {
      if (!timeStr) return 0;
      const match24 = timeStr.match(/^(\d{1,2}):/);
      return match24 ? parseInt(match24[1], 10) : 0;
    },
    /**
     * Resolve the time field on a trip according to the active reference-time toggle.
     *  - 'firstStopDeparture' -> trip.scheduledStart
     *  - 'lastStopArrival'    -> trip.scheduledEnd, or nothing
     *
     * In arrival mode a trip without `scheduledEnd` has no known arrival, and it used to
     * fall back to `scheduledStart` — which placed and kept trips by their departure while
     * the UI claimed arrival. A 06:30–06:59 arrival window then listed a route departing
     * 06:45 and arriving 07:53 (VSER-14676). An unknown arrival is now left out instead.
     *
     * Scope: this toggle ONLY changes hour-level bucketing
     * (`routeByRunData`, `singleRouteByRunData`, `occupancyByRunData`, and the
     * single-day branch of `dateBucketKey`). It is intentionally a no-op for
     * "by Weekday" (`routeByDayData`) and multi-day "by Date" buckets, which
     * are anchored on the BE-provided `trip.dayOfWeek` / `trip.date` fields.
     * For same-day trips (start and end on the same calendar day) the weekday
     * and date are identical regardless of which time field is referenced, so
     * re-deriving them from this helper would have no observable effect on
     * the current dataset (no cross-midnight trips).
     */
    tripReferenceTime(trip) {
      if (this.routeFilters.referenceTime === 'lastStopArrival') {
        return trip.scheduledEnd || null;
      }
      return trip.scheduledStart;
    },
    /**
     * FE-only time-window filter, scoped to `byLastStopArrival` mode.
     *
     * For `byFirstStopDeparture` the BE has already filtered trips by
     * `scheduledStart ∈ [fromTime, toTime]`, so this guard is a no-op there
     * (re-running the same predicate would only risk over-restricting if the
     * BE/FE clocks ever drift).
     *
     * For `byLastStopArrival` `fetchRouteTrips` deliberately omits the BE
     * fromTime/toTime params; we then keep only trips whose `scheduledEnd`
     * lies inside the user-selected window — which is the spec definition of
     * "trips arriving at their last stop in the selected interval".
     *
     * A trip whose `scheduledEnd` is missing or unreadable cannot be said to arrive in the
     * window, so it is dropped rather than judged by its departure (VSER-14676).
     *
     * by-Weekday hides the time-range UI per spec, so the filter is also a
     * no-op there to avoid silently dropping rows when the toggle is left in
     * lastStopArrival from a previous view.
     */
    tripInTimeWindow(trip) {
      if (this.routeFilters.referenceTime !== 'lastStopArrival') return true;
      if (this.routeFilters.routeViewMode === 'dayOfWeek') return true;
      const { fromTime, toTime } = this.routeFilters;
      if (!fromTime && !toTime) return true;
      const ref = trip.scheduledEnd;
      if (!ref) return false;
      const toMins = (s) => {
        if (!s) return null;
        const mm = s.match(/^(\d{1,2}):(\d{2})/);
        return mm ? parseInt(mm[1], 10) * 60 + parseInt(mm[2], 10) : null;
      };
      const tripMin = toMins(ref);
      if (tripMin == null) return false;
      const fromMin = toMins(fromTime);
      const toMin = toMins(toTime);
      if (fromMin != null && tripMin < fromMin) return false;
      if (toMin != null && tripMin > toMin) return false;
      return true;
    },
    /**
     * Map a backend dayOfWeek constant ("MONDAY", "TUESDAY", …) to its localized
     * full name via flexigo.common.<day>. Falls back to title-case if no key.
     */
    localizedDayName(dayStr) {
      const key = dayStr?.toLowerCase();
      const localized = key ? this.$t(`flexigo.common.${key}`) : '';
      // i18n returns the key itself when missing; detect that and fall back.
      if (localized && localized !== `flexigo.common.${key}`) return localized;
      return dayStr ? dayStr.charAt(0).toUpperCase() + dayStr.slice(1).toLowerCase() : dayStr;
    },
    /**
     * Stops of a trip that can take boardings: every stop except the trip's
     * terminal stop, where all remaining riders alight. The analysis doc
     * requires the terminal stop to be hidden from the single-route chart
     * and its tooltip (VSER-12588 QA finding 2). BE returns `trip.stops`
     * sorted by stopSequence, so dropping the last entry is reliable.
     */
    tripBoardingStops(trip) {
      const stops = trip?.stops || [];
      return stops.length > 1 ? stops.slice(0, -1) : stops;
    },
    handleRouteDateChange(dateRange) {
      // dateRange emits: { type, fromDate, toDate, startDay, endDay }
      // fromDate/toDate are strings like '2026-04-01', startDay/endDay are integers like 20260401.
      // type is one of 'today' | 'yesterday' | 'thisWeek' | 'lastSevenDays' | 'thisMonth' |
      // 'lastThirtyDays' | 'lastMonth' | 'customDate'.
      this.routeFilters.dateRange = toCardDateRange(dateRange);
    },
    handleRouteChange(routeIds) {
      // Picking routes ends the "no routes" state; clearing them afterwards means every route.
      if (routeIds.length) this.routeCardNoRoutes = false;
      this.routeFilters.routeIds = routeIds;
    },
    onWorkingTypeChange({ index }) {
      const types = ['All', 'Fixed', 'Dynamic'];
      this.selectedWorkingType = types[index];
    },
    onStopWorkingTypeChange({ index }) {
      const types = ['All', 'Fixed', 'Dynamic'];
      this.selectedStopWorkingType = types[index];
    },
    handleRouteDirectionChange(direction) {
      this.routeFilters.direction = direction;
    },
    setRouteViewMode(mode) {
      this.routeFilters.routeViewMode = mode;
    },
    handleRouteReferenceTimeChange(refTime) {
      this.routeFilters.referenceTime = refTime;
    },
    handleRouteTimeRangeChange({ fromTime, toTime }) {
      this.routeFilters.fromTime = fromTime;
      this.routeFilters.toTime = toTime;
    },
    handleIntervalChange(interval) {
      this.routeFilters.interval = interval;
    },
    /**
     * Returns bucket size in minutes for the active interval toggle.
     * '15min' -> 15, '30min' -> 30, '1hour' -> 60.
     */
    intervalMinutes() {
      const map = { '15min': 15, '30min': 30, '1hour': 60 };
      return map[this.routeFilters.interval] || 60;
    },
    /**
     * Floor a "HH:mm" time string to the start of its interval bucket.
     * e.g. interval=30 → "08:42" -> "08:30"; interval=15 → "08:42" -> "08:30"; interval=60 → "08:42" -> "08:00".
     * Returns null if timeStr is empty/invalid.
     */
    bucketTimeByInterval(timeStr, intervalMin) {
      if (!timeStr) return null;
      const m = timeStr.match(/^(\d{1,2}):(\d{2})/);
      if (!m) return null;
      const hour = parseInt(m[1], 10);
      const minute = parseInt(m[2], 10);
      const totalMin = hour * 60 + minute;
      const bucketStartMin = Math.floor(totalMin / intervalMin) * intervalMin;
      const bh = Math.floor(bucketStartMin / 60);
      const bm = bucketStartMin % 60;
      return `${String(bh).padStart(2, '0')}:${String(bm).padStart(2, '0')}`;
    },
    /**
     * For the single-route by-time-of-day view: pick the exact scheduled time
     * to bucket on, based on the trip's direction.
     *  - INCOMING (inbound)  -> trip.scheduledEnd   (arrival)
     *  - OUTGOING (outbound) -> trip.scheduledStart (departure)
     *  - LOOP / other        -> trip.scheduledStart (departure as fallback)
     * Returns the raw HH:mm string (no bucketing), so each distinct scheduled
     * time becomes its own x-axis bar — per spec line 69.
     */
    singleRouteTimeKey(trip) {
      if (trip?.direction === 'INCOMING') {
        return trip.scheduledEnd || trip.scheduledStart || null;
      }
      return trip?.scheduledStart || null;
    },
    dateBucketKey(trip, span) {
      if (span <= 1) {
        const hour = this.extractHour(this.tripReferenceTime(trip));
        return `H${String(hour).padStart(2, '0')}`;
      }
      if (span <= 31) {
        return this.normalizeDateKey(trip.date);
      }
      if (span <= 180) {
        const d = this.parseTripDate(trip.date);
        const day = d.getDay();
        const mondayOffset = day === 0 ? -6 : 1 - day;
        const monday = new Date(d);
        monday.setDate(d.getDate() + mondayOffset);
        return `W${format(monday, 'yyyyMMdd')}`;
      }
      const d = this.parseTripDate(trip.date);
      return `M${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    },
    /**
     * Human-readable label for a date bucket key.
     *
     * `short: true` yields the compact, year-less form used for the x-axis, so
     * a 30-day range stays legible instead of Highcharts dropping every other
     * label ("21/7" instead of "21/07/2026"). The long form is kept for the
     * tooltip header, which has room for the full date. Only the daily bucket
     * has two forms — hour, week and month labels are already compact.
     *
     * Both forms follow the browser locale, so an English-browser admin gets
     * '7/21' / '7/21/2026' and a Turkish one '21/7' / '21/07/2026'.
     */
    dateBucketLabel(key, span, { short = false } = {}) {
      const locale = this.$i18n?.locale || 'en';
      const monthsEN = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const monthsTR = ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'];
      const months = locale === 'tr' ? monthsTR : monthsEN;
      if (span <= 1) {
        // A single day is bucketed by hour, so this "date" label is a clock time and follows
        // the browser locale like every other time on the dashboard — the chart's axis
        // formatter passes an already-formatted one through untouched (VSER-14676).
        return localFormatTimeString(`${key.slice(1)}:00`);
      }
      if (span <= 31) {
        const d = this.parseTripDate(key);
        return format(d, getBrowserLocaleFormat({ type: short ? 'dateShort' : 'date' }));
      }
      if (span <= 180) {
        const monday = this.parseTripDate(key.slice(1));
        const sunday = new Date(monday);
        sunday.setDate(monday.getDate() + 6);
        const m1 = months[monday.getMonth()];
        const m2 = months[sunday.getMonth()];
        if (m1 === m2) return `${m1} ${monday.getDate()}-${sunday.getDate()}`;
        return `${m1} ${monday.getDate()}-${m2} ${sunday.getDate()}`;
      }
      const [, yearStr, monthStr] = key.match(/^M(\d{4})-(\d{2})$/);
      return `${months[parseInt(monthStr, 10) - 1]} ${yearStr}`;
    },
    normalizeDateKey(dateStr) {
      // Accepts "DD.MM.YYYY", "YYYY-MM-DD", "MM-DD-YYYY", or "YYYYMMDD" (string or number), returns "YYYYMMDD"
      const s = String(dateStr);
      // DD.MM.YYYY (e.g. "20.04.2026")
      const dotMatch = s.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
      if (dotMatch) {
        const [, dd, mm, yyyy] = dotMatch;
        return `${yyyy}${mm.padStart(2, '0')}${dd.padStart(2, '0')}`;
      }
      // YYYY-MM-DD (ISO, e.g. "2026-04-20")
      const isoMatch = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
      if (isoMatch) {
        const [, yyyy, mm, dd] = isoMatch;
        return `${yyyy}${mm.padStart(2, '0')}${dd.padStart(2, '0')}`;
      }
      // MM-DD-YYYY (US, e.g. "05-01-2026")
      const usDashMatch = s.match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/);
      if (usDashMatch) {
        const [, mm, dd, yyyy] = usDashMatch;
        return `${yyyy}${mm.padStart(2, '0')}${dd.padStart(2, '0')}`;
      }
      // YYYYMMDD or fallback
      return s.replace(/-/g, '').substring(0, 8);
    },
    parseTripDate(dateStr) {
      const s = this.normalizeDateKey(dateStr);
      const year = parseInt(s.substring(0, 4), 10);
      const month = parseInt(s.substring(4, 6), 10) - 1;
      const day = parseInt(s.substring(6, 8), 10);
      return new Date(year, month, day);
    },
    generateAllBucketKeys(span) {
      const { dateRange } = this.routeFilters;
      if (!dateRange?.startDate || !dateRange?.endDate) return [];
      const { startDate, endDate } = dateRange;
      const keys = [];
      if (span <= 1) {
        for (let h = 0; h < 24; h += 1) {
          keys.push(`H${String(h).padStart(2, '0')}`);
        }
      } else if (span <= 31) {
        const cursor = new Date(startDate);
        while (cursor <= endDate) {
          const y = cursor.getFullYear();
          const m = String(cursor.getMonth() + 1).padStart(2, '0');
          const d = String(cursor.getDate()).padStart(2, '0');
          keys.push(`${y}${m}${d}`);
          cursor.setDate(cursor.getDate() + 1);
        }
      } else if (span <= 180) {
        const cursor = new Date(startDate);
        const dayOfWeek = cursor.getDay();
        const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
        cursor.setDate(cursor.getDate() + mondayOffset);
        while (cursor <= endDate) {
          keys.push(`W${format(cursor, 'yyyyMMdd')}`);
          cursor.setDate(cursor.getDate() + 7);
        }
      } else {
        const cursor = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
        while (cursor <= endDate) {
          keys.push(`M${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, '0')}`);
          cursor.setMonth(cursor.getMonth() + 1);
        }
      }
      return keys;
    },
    // Stop filter handlers
    handleStopDateChange(dateRange) {
      this.stopFilters.dateRange = toCardDateRange(dateRange);
    },
    handleStopChange(stopIds) {
      this.stopFilters.stopIds = stopIds;
    },
    handleStopRouteChange(routeIds) {
      if (routeIds.length) this.stopCardNoRoutes = false;
      this.stopFilters.routeIds = routeIds;
    },
    handleStopTimeRangeChange({ fromTime, toTime }) {
      this.stopFilters.fromTime = fromTime;
      this.stopFilters.toTime = toTime;
    },
    handleStopMetricChange({ option }) {
      this.stopFilters.stopMetric = option.value;
    },
    toggleStopSort() {
      this.stopFilters.stopSortAsc = !this.stopFilters.stopSortAsc;
    },
    // Export (Excel report; chart images use Highcharts' own export menu)
    async handleExportExcel() {
      // No routes means no data; an export with empty routeIds would hold every route.
      if (this.exporting || this.routeCardNoRoutes) return;
      this.exporting = true;
      try {
        const { startDay, endDay } = this.routeFilters.dateRange || {};
        const { routeIds, direction } = this.routeFilters;
        await this.exportExcel({ startDay, endDay, routeIds, direction });
      } finally {
        this.exporting = false;
      }
    },
    // The BE only offers the route-level ridership Excel report, so the stop
    // card exports it scoped to this card's own date range and route filter.
    async handleStopExportExcel() {
      if (this.stopExporting || this.stopCardNoRoutes) return;
      this.stopExporting = true;
      try {
        const { startDay, endDay } = this.stopFilters.dateRange || {};
        const { routeIds } = this.stopFilters;
        await this.exportExcel({ startDay, endDay, routeIds, direction: 'ALL' });
      } finally {
        this.stopExporting = false;
      }
    },
  },
};
</script>

<style scoped>
.ridership-dashboard-module {
  /* Transparent wrapper — it only owns the outer top/bottom gap to the sibling
     dashboard modules (occupancyGraph, performanceGraph, …), all of which wrap
     themselves in `.out-countainer.custom-border.mt-3`. Each card below is its
     own white panel, so the `mt-3` gap between them shows the grey page
     background and the two charts read as separate cards (per QA: they must not
     look "bitişik"/fused into one panel). */
  margin-top: 1rem;
  margin-bottom: 20px;
}

/* Each ridership chart is a self-contained white card, mirroring the look of the
   sibling dashboard modules (white fill + light border + rounded corners). */
.rd-card {
  background: #fff;
  border: 1px solid #e6eaee;
  border-radius: 8px;
}

.rd-legend-swatch {
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 2px;
  flex-shrink: 0;
}

/* Diagonal stripe matching the On bar's Highcharts pattern fill:
   4×4 tile, 1px #687692 lines on #f6f7f9 background, ~2px gap.
   Stripes run bottom-left to top-right (matching the bar's M 0 3 L 3 0 path).
   CSS -45deg produces stripes along the 45° axis (bottom-left → top-right). */
.rd-legend-swatch--striped {
  background-color: #bcc2cd;
  background-image: repeating-linear-gradient(-45deg, #687692 0, #687692 1px, transparent 1px, transparent 3px);
}

.rd-legend-label {
  font-size: 12px;
  color: #666;
}

.rd-sort-toggle {
  cursor: pointer;
  font-size: 13px;
  padding: 4px 12px;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  user-select: none;
  background: #f8f9fa;
}

.rd-sort-toggle:hover {
  background: #e9ecef;
}
</style>
